'use client';

import { useEffect, useRef, useState } from 'react';

interface HeroVideoBackdropProps {
    desktopSrc: string;
    desktopPoster: string;
}

const DESKTOP_QUERY = '(min-width: 768px)';

/**
 * Motion layer over the hero. The <picture> in ProfileHero is the LCP element;
 * this fades in on top once it can show a frame, so the swap from still to motion
 * is never a pop-in or a stall.
 *
 * Two things this has to avoid, both measured on production (Trello P-01):
 *
 * 1. It used to render *both* videos and hide one with `md:hidden`. A hidden
 *    <video autoPlay> still downloads, so mobile pulled 1,556 KB + 1,358 KB —
 *    2.9 MB, ~60% of the page — for one clip it could show. Only the variant
 *    matching the viewport was mounted afterwards. Mobile now keeps the hero's
 *    optimized still image instead: the 1.6 MB portrait loop added no content,
 *    consumed most of the mobile transfer budget, and cost battery/data. Motion
 *    remains on desktop where the bandwidth and CPU budget are much healthier.
 *
 * 2. `preload="metadata"` does not restrain an autoplaying video; autoplay forces
 *    a full fetch regardless. So the source is withheld entirely until the page has
 *    loaded and the main thread is idle. Until then the poster (already the LCP
 *    image, so already cached) holds the frame — visually identical, just no longer
 *    racing the thing it sits on top of.
 */
export default function HeroVideoBackdrop({
    desktopSrc,
    desktopPoster,
}: HeroVideoBackdropProps) {
    const ref = useRef<HTMLVideoElement>(null);
    const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
    const [shouldLoad, setShouldLoad] = useState(false);
    const [ready, setReady] = useState(false);

    // Pick the variant on the client and keep it correct across rotation/resize.
    useEffect(() => {
        const mq = window.matchMedia(DESKTOP_QUERY);
        const sync = () => setIsDesktop(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    // Hold the source back until the page has finished loading and the browser has
    // a spare moment — that is what keeps 1.5 MB of video off the LCP critical path.
    useEffect(() => {
        if (!isDesktop) return;

        let idleHandle: number | undefined;

        const queue = () => {
            const ric = window.requestIdleCallback;
            if (typeof ric === 'function') {
                idleHandle = ric(() => setShouldLoad(true), { timeout: 3000 });
            } else {
                idleHandle = window.setTimeout(() => setShouldLoad(true), 1200);
            }
        };

        if (document.readyState === 'complete') {
            queue();
            return () => cancelIdle(idleHandle);
        }

        window.addEventListener('load', queue, { once: true });
        return () => {
            window.removeEventListener('load', queue);
            cancelIdle(idleHandle);
        };
    }, [isDesktop]);

    // A fast/cached video can reach HAVE_FUTURE_DATA before this effect runs, in
    // which case 'canplay' already fired and an onCanPlay prop would never see it —
    // so check readyState directly as well.
    useEffect(() => {
        const video = ref.current;
        if (!video || !shouldLoad) return;

        if (video.readyState >= 3) {
            setReady(true);
            return;
        }
        const markReady = () => setReady(true);
        video.addEventListener('canplay', markReady);
        video.addEventListener('loadeddata', markReady);
        return () => {
            video.removeEventListener('canplay', markReady);
            video.removeEventListener('loadeddata', markReady);
        };
    }, [shouldLoad, isDesktop]);

    // Nothing to render server-side: the poster is already painted by the hero
    // <picture> underneath, so there is no visual gap.
    if (!isDesktop) return null;

    return (
        <video
            key={desktopSrc}
            ref={ref}
            aria-hidden="true"
            muted
            autoPlay
            loop
            playsInline
            preload="none"
            poster={desktopPoster}
            {...(shouldLoad ? { src: desktopSrc } : {})}
            className={`absolute inset-0 -z-20 h-full w-full object-cover object-center transition-opacity duration-700 ${ready ? 'opacity-100' : 'opacity-0'}`}
        />
    );
}

function cancelIdle(handle?: number) {
    if (handle === undefined) return;
    if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(handle);
    else window.clearTimeout(handle);
}
